import { useState, useEffect, useRef } from "react";
import { X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useToast } from "@/hooks/use-toast";
import {
  calculateCreditPrice,
  formatPrice,
  validateCreditAmount,
} from "@/utils/creditUtils";
import { useAuth } from "@/hooks/useAuth";
import {
  createCreditCheckoutSession,
  redirectToCheckout,
  CheckoutError,
} from "@/services/stripeService";
import { OrderSummary } from "./OrderSummary";
import type { CreditPackage } from "@/types/credits";

/**
 * Composant BuyCreditModal - Modale d'achat de crédits (Design Figma)
 * 
 * Design moderne avec :
 * - Layout horizontal : contenu gauche + récapitulatif droite
 * - Bandeau solde actuel avec gradient sombre
 * - 4 packs prédéfinis (10, 25, 50, 100) avec badge "MEILLEUR CHOIX"
 * - Slider interactif avec bulle de valeur
 * - Récapitulatif avec TVA et total TTC
 * - Prix barrés et pourcentages d'économie
 * 
 * Requirements: 4.1, 4.2, 4.3, 4.4
 */
interface BuyCreditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Prix de base par crédit (15€) pour calculer les prix barrés
const BASE_PRICE_PER_CREDIT = 1500;

export const BuyCreditModal = ({
  open,
  onOpenChange,
}: BuyCreditModalProps) => {
  const { profile } = useAuth();
  const organizationId = profile?.organization_id || undefined;
  const { toast } = useToast();

  // État pour le montant sélectionné
  const [selectedAmount, setSelectedAmount] = useState<number>(25); // Défaut: pack 25
  const [selectedPackage, setSelectedPackage] = useState<number | null>(25); // Pack sélectionné
  const [sliderValue, setSliderValue] = useState<number>(25);
  
  // État de chargement pendant la redirection
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  // Référence pour le slider
  const sliderRef = useRef<HTMLInputElement>(null);

  // Packs prédéfinis : 4 packs selon le design Figma
  const presetPackages: CreditPackage[] = [
    { 
      credits: 10, 
      price: calculateCreditPrice(10), 
      pricePerCredit: calculateCreditPrice(10) / 10,
      originalPrice: 10 * BASE_PRICE_PER_CREDIT,
      savings: (10 * BASE_PRICE_PER_CREDIT) - calculateCreditPrice(10),
      savingsPercentage: Math.round((((10 * BASE_PRICE_PER_CREDIT) - calculateCreditPrice(10)) / (10 * BASE_PRICE_PER_CREDIT)) * 100),
      isPopular: false,
      label: "Prix standard"
    },
    { 
      credits: 25, 
      price: calculateCreditPrice(25), 
      pricePerCredit: calculateCreditPrice(25) / 25,
      originalPrice: 25 * BASE_PRICE_PER_CREDIT,
      savings: (25 * BASE_PRICE_PER_CREDIT) - calculateCreditPrice(25),
      savingsPercentage: Math.round((((25 * BASE_PRICE_PER_CREDIT) - calculateCreditPrice(25)) / (25 * BASE_PRICE_PER_CREDIT)) * 100),
      isPopular: true,
      label: "Économisez"
    },
    { 
      credits: 50, 
      price: calculateCreditPrice(50), 
      pricePerCredit: calculateCreditPrice(50) / 50,
      originalPrice: 50 * BASE_PRICE_PER_CREDIT,
      savings: (50 * BASE_PRICE_PER_CREDIT) - calculateCreditPrice(50),
      savingsPercentage: Math.round((((50 * BASE_PRICE_PER_CREDIT) - calculateCreditPrice(50)) / (50 * BASE_PRICE_PER_CREDIT)) * 100),
      isPopular: false,
      label: "Économisez"
    },
    { 
      credits: 100, 
      price: calculateCreditPrice(100), 
      pricePerCredit: calculateCreditPrice(100) / 100,
      originalPrice: 100 * BASE_PRICE_PER_CREDIT,
      savings: (100 * BASE_PRICE_PER_CREDIT) - calculateCreditPrice(100),
      savingsPercentage: Math.round((((100 * BASE_PRICE_PER_CREDIT) - calculateCreditPrice(100)) / (100 * BASE_PRICE_PER_CREDIT)) * 100),
      isPopular: false,
      label: "Économisez"
    },
  ];

  // Calculer le prix pour le montant sélectionné
  const selectedPrice = calculateCreditPrice(selectedAmount);

  // Réinitialiser la sélection quand la modale s'ouvre
  useEffect(() => {
    if (open) {
      setSelectedAmount(25);
      setSelectedPackage(25);
      setSliderValue(25);
      setIsProcessing(false);
    }
  }, [open]);

  // Gérer la sélection d'un pack prédéfini
  const handlePackageSelect = (pkg: CreditPackage) => {
    setSelectedAmount(pkg.credits);
    setSelectedPackage(pkg.credits);
    setSliderValue(pkg.credits);
  };

  // Gérer le changement du slider
  const handleSliderChange = (value: number) => {
    setSelectedAmount(value);
    setSliderValue(value);
    setSelectedPackage(null); // Désélectionner les packs
  };

  // Gérer l'achat
  const handlePurchase = async () => {
    if (!selectedAmount || !organizationId) return;

    // Valider
    const validation = validateCreditAmount(selectedAmount);
    if (!validation.valid) {
      toast({
        title: "Montant invalide",
        description: validation.error,
        variant: "destructive",
      });
      return;
    }

    try {
      setIsProcessing(true);

      // Créer la session de checkout Stripe
      const { checkout_url } = await createCreditCheckoutSession({
        type: 'credits',
        organizationId,
        creditQuantity: selectedAmount,
        successUrl: `${window.location.origin}/credits/success`,
        cancelUrl: `${window.location.origin}/credits/cancel`,
      });

      // Rediriger vers Stripe Checkout
      redirectToCheckout(checkout_url);
    } catch (error) {
      console.error('[BuyCreditModal] Error during purchase:', error);
      setIsProcessing(false);

      if (error instanceof CheckoutError) {
        toast({
          title: "Erreur lors de l'achat",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Erreur inattendue",
          description: "Une erreur est survenue lors de la création de la session de paiement. Veuillez réessayer.",
          variant: "destructive",
        });
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[90vw] sm:max-w-[85vw] lg:max-w-[950px] h-auto max-h-[96vh] bg-[#fafafa] p-0 [&>button]:hidden overflow-hidden">
        {/* Accessibilité : Titre et description cachés visuellement */}
        <VisuallyHidden>
          <DialogTitle>Acheter des crédits</DialogTitle>
          <DialogDescription>
            Sélectionnez le nombre de crédits que vous souhaitez acheter avec notre système de prix dégressif
          </DialogDescription>
        </VisuallyHidden>

        <div className="max-h-[96vh] overflow-y-auto px-8 py-6">
          {/* Header avec bouton fermer personnalisé */}
          <div className="flex justify-end -mt-2 -mr-2 mb-4">
            <button
              onClick={() => onOpenChange(false)}
              className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-[10px] flex items-center justify-center text-gray-600 hover:text-gray-900 transition-all"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Section titre */}
          <div className="mb-5">
            <h1 className="text-3xl sm:text-4xl lg:text-[42px] font-light tracking-[-2px] text-[#1A212B] mb-1">
              Acheter des crédits
            </h1>
            <p className="text-sm sm:text-base text-gray-600">
              Sélectionnez le nombre de crédits que vous souhaitez acheter
            </p>
          </div>

          {/* Layout 2 colonnes */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
            {/* Colonne gauche */}
            <div className="space-y-4">
              {/* Affichage prix + Slider */}
              <div className="bg-white rounded-[20px] p-5 shadow-sm">
                <div className="text-4xl sm:text-5xl lg:text-[56px] font-light tracking-[-3px] text-[#1A212B] mb-5">
                  {formatPrice(selectedPrice)}
                </div>
                
                {/* Slider avec bulle */}
                <div className="relative pt-12 pb-2">
                  <div 
                    className="absolute top-0 bg-[#1A212B] text-white px-3 py-1 rounded-lg text-xs sm:text-sm font-semibold transition-all z-10"
                    style={{
                      left: `calc(${((sliderValue - 1) / 199) * 100}% - 20px)`,
                    }}
                  >
                    {sliderValue}
                    <div className="absolute bottom-[-4px] left-1/2 -translate-x-1/2 w-2 h-2 bg-[#1A212B] rotate-45"></div>
                  </div>
                  
                  <input
                    ref={sliderRef}
                    type="range"
                    min="1"
                    max="200"
                    value={sliderValue}
                    onChange={(e) => handleSliderChange(Number(e.target.value))}
                    className="w-full h-1.5 bg-gray-200 rounded-full appearance-none cursor-pointer slider-custom"
                    style={{
                      background: `linear-gradient(to right, #40E0D0 0%, #40E0D0 ${((sliderValue - 1) / 199) * 100}%, #e5e7eb ${((sliderValue - 1) / 199) * 100}%, #e5e7eb 100%)`
                    }}
                  />
                </div>
              </div>

              {/* Packs */}
              <div>
                <p className="text-base text-[#1A212B] font-medium italic mb-3">
                  Ou sélectionnez nos packs
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {presetPackages.map((pkg) => {
                  const isSelected = selectedPackage === pkg.credits;
                  const isPopular = pkg.isPopular;
                  
                  return (
                    <div
                      key={pkg.credits}
                      onClick={() => handlePackageSelect(pkg)}
                      className={`
                        relative rounded-2xl p-5 transition-all hover:-translate-y-1 hover:shadow-xl min-h-[240px] flex flex-col w-full cursor-pointer
                        ${isSelected 
                          ? 'bg-gradient-to-br from-[#1a4a47] to-[#0d2827] border-3 border-[#40E0D0]' 
                          : 'bg-[#1A212B] border-3 border-transparent'
                        }
                      `}
                    >
                      {/* Badge MEILLEUR CHOIX - centré et dépassant vers le haut */}
                      {isPopular && (
                        <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-[#40E0D0] text-white px-2.5 py-1 rounded-md text-[9px] font-semibold uppercase tracking-wide whitespace-nowrap shadow-md">
                          Meilleur choix
                        </div>
                      )}
                      
                      {/* Header avec radio + crédits */}
                      <div className="flex items-center gap-2.5 mb-4">
                        <div className={`
                          w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all flex-shrink-0
                          ${isSelected ? 'border-[#40E0D0] bg-[#40E0D0]' : 'border-white/30'}
                        `}>
                          {isSelected && (
                            <div className="w-1.5 h-1.5 bg-[#1A212B] rounded-full"></div>
                          )}
                        </div>
                        <div className="text-xl font-semibold text-white">
                          {pkg.credits}
                        </div>
                      </div>
                      
                      {/* Prix */}
                      <div className="text-2xl sm:text-3xl font-light tracking-[-1px] text-white mb-1.5">
                        {formatPrice(pkg.price)}
                      </div>
                      
                      {/* Prix barré */}
                      <div className="text-sm sm:text-base text-white/40 line-through mb-2.5">
                        {formatPrice(pkg.originalPrice || 0)}
                      </div>
                      
                      {/* Discount */}
                      <div className={`text-xs mb-4 ${isSelected ? 'text-[#40E0D0]' : 'text-white/60'}`}>
                        {pkg.label === "Prix standard" ? pkg.label : `${pkg.label} ${pkg.savingsPercentage}%`}
                      </div>
                      
                      {/* Texte d'action (plus de bouton imbriqué) */}
                      <div className={`
                        w-full py-3 rounded-[10px] text-sm font-semibold transition-all text-center mt-auto
                        ${isSelected ? 'bg-[#40E0D0] text-[#1A212B]' : 'bg-white text-[#1A212B]'}
                      `}>
                        Acheter maintenant
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            </div>

            {/* Colonne droite - Récapitulatif */}
            <OrderSummary
              credits={selectedAmount}
              priceHT={selectedPrice}
              onProceedToPayment={handlePurchase}
              isProcessing={isProcessing}
              disabled={!selectedAmount || !organizationId}
            />
          </div>
        </div>

        <style>{`
          .slider-custom::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            width: 32px;
            height: 32px;
            background: #1A212B;
            border-radius: 50%;
            cursor: pointer;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
          }
          
          .slider-custom::-moz-range-thumb {
            width: 32px;
            height: 32px;
            background: #1A212B;
            border-radius: 50%;
            cursor: pointer;
            border: none;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
};
